import { memo } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import dayjs from 'dayjs';
import LumeTooltip from 'components/LumeTooltip';
import { transformLabResultUnit } from 'data/dutchie/transformations';
import getPercentOrMg from 'utils/get-percent-or-mg';
import styles from './ProductCannabis.module.scss';

const ProductCannabis = ({
  category,
  classification,
  effect,
  packagedDateTooltipInfo,
  selectedVariant,
  packagedDateConfiguration,
  packagedDate,
  terpeneTooltipInfo,
}) => {
  const showTerpeneInfo = useSelector(
    (state) => state.featureFlags.terpene_info
  );
  const showPackagedDateFlag = useSelector(
    (state) => state.featureFlags.packaged_date
  );
  const isTHC =
    category !== 'flower' &&
    (selectedVariant?.percentTHC > 0 || selectedVariant?.mgTHC > 0);
  const isTerpenes = showTerpeneInfo && selectedVariant?.totalTerpenes > 0;
  const isCBD = selectedVariant?.percentCBD > 0 || selectedVariant?.mgCBD > 0;
  const isType = Boolean(classification);
  const isEffect = Boolean(effect);

  const {
    label: packagedDateLabel,
    max_age_days: packagedDateMaxDays,
    categories: packagedDateCategories = [],
  } = packagedDateConfiguration;

  const isPackagedDate = Boolean(
    showPackagedDateFlag &&
      packagedDateLabel &&
      packagedDate &&
      (!packagedDateMaxDays ||
        dayjs(packagedDate).add(packagedDateMaxDays, 'day') >= dayjs()) &&
      packagedDateCategories.includes(category)
  );
  const formattedPackagedDate = dayjs(packagedDate).format('M/D/YY');

  const columnCount = [
    isTHC,
    isTerpenes || isCBD,
    isType,
    isEffect,
    isPackagedDate,
  ].filter(Boolean).length;

  if (columnCount === 0) {
    return null; // we have nothing to render
  }

  const thc = getPercentOrMg(
    selectedVariant?.percentTHC,
    selectedVariant?.mgTHC
  );
  const cbd = getPercentOrMg(
    selectedVariant?.percentCBD,
    selectedVariant?.mgCBD
  );
  const terpenes = `${selectedVariant?.totalTerpenes}${transformLabResultUnit(
    selectedVariant?.totalTerpenesUnit
  )}`;

  return (
    <section className="font-roboto mb-15">
      <div
        className={classNames('row justify-content-between w-100', {
          'w-md-100': columnCount === 1 || columnCount >= 4,
          'w-md-50': columnCount === 2 || columnCount === 3,
        })}
        data-value="product-data-table"
      >
        {isTHC && (
          <div className="col">
            <h4 className={styles.title}>THC</h4>
            <span className={styles.label}>{thc}</span>
          </div>
        )}
        {isTerpenes && (
          <div className="col">
            <div className="d-flex flex-row ">
              <h4 className={styles.title}>TERPS</h4>
              {terpeneTooltipInfo && (
                <div className="ps-2">
                  <LumeTooltip
                    iconColorClass="text-black"
                    tooltipPlacement="top"
                  >
                    <span>{terpeneTooltipInfo}</span>
                  </LumeTooltip>
                </div>
              )}
            </div>
            <span className={styles.label}>{terpenes}</span>
          </div>
        )}
        {isCBD && !isTerpenes && (
          <div className="col">
            <h4 className={styles.title}>CBD</h4>
            <span className={styles.label}>{cbd}</span>
          </div>
        )}
        {isType && (
          <div className="col">
            <h4 className={styles.title}>Type</h4>
            <span className={styles.label}>{classification}</span>
          </div>
        )}
        {isEffect && (
          <div className="col">
            <h4 className={styles.title}>Effect</h4>
            <span className={styles.label}>{effect}</span>
          </div>
        )}
        {isPackagedDate && (
          <div className="col">
            <div className="d-flex flex-row ">
              <h4 className={styles.title}>{packagedDateLabel}</h4>
              {packagedDateTooltipInfo && (
                <div className="ps-2">
                  <LumeTooltip
                    iconColorClass="text-black"
                    tooltipPlacement="top"
                  >
                    <span>{packagedDateTooltipInfo}</span>
                  </LumeTooltip>
                </div>
              )}
            </div>
            <span className={styles.label}>{formattedPackagedDate}</span>
          </div>
        )}
      </div>
    </section>
  );
};

export default memo(ProductCannabis);
